import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { formatCurrency, cn } from '../../lib/utils';
import { invoicePaidAmount } from '../../lib/invoicePaid';

/**
 * Diagnostics card: shows a target month under both date bases side by side.
 *
 * Purpose: let Fan see that (e.g.) 183k/42k by due-date and 161k/0 by
 * issue-date are the same underlying data on two bases, and confirm paid
 * is computed from amount_paid on every surface.
 *
 * Props destructured in body (babel-metadata-plugin workaround).
 */
export function InvoiceReconciliationCard(props) {
  const { invoices, cxInv, dc, targetMonth } = props;
  const [showDetail, setShowDetail] = useState(false);

  const recon = useMemo(() => {
    if (!invoices?.length || !targetMonth) return null;

    const mk = targetMonth; // e.g. "2026-06"

    // Partition invoices relative to the target month
    const dueInMonth = [];       // due_date in target month (regardless of issue)
    const issuedInMonth = [];    // issue_date in target month (regardless of due)
    const issuedBeforeDueIn = []; // issued before month, due in month
    const issuedInDueLater = [];  // issued in month, due after month or no due
    const noDueDate = [];         // no due_date at all
    const noIssueDate = [];       // no issue_date at all
    const dueVisFallback = [];    // no due_date, issue_date in month (appears in due-date surface via fallback)

    for (const inv of invoices) {
      const issueMk = inv.issue_date ? inv.issue_date.slice(0, 7) : null;
      const dueMk = inv.due_date ? inv.due_date.slice(0, 7) : null;
      const dueBucket = inv.due_date || inv.issue_date;
      const dueBucketMk = dueBucket ? dueBucket.slice(0, 7) : null;

      if (!inv.due_date) noDueDate.push(inv);
      if (!inv.issue_date) noIssueDate.push(inv);

      if (dueBucketMk === mk) dueInMonth.push(inv);
      if (issueMk === mk) issuedInMonth.push(inv);

      // Cross-basis decomposition
      if (issueMk && issueMk < mk && dueBucketMk === mk) {
        issuedBeforeDueIn.push(inv);
      }
      if (issueMk === mk && dueBucketMk && dueBucketMk !== mk) {
        issuedInDueLater.push(inv);
      }
      if (!inv.due_date && issueMk === mk) {
        dueVisFallback.push(inv);
      }
    }

    // Ex VAT (2026-10-25): same basis as the Cashflow tab and the charts above.
    function net(inv) {
      return inv.subtotal != null && inv.subtotal !== '' ? (Number(inv.subtotal) || 0) : (Number(inv.total_amount) || 0);
    }
    function sumField(arr, field) {
      return arr.reduce((s, inv) => s + cxInv(field === 'total_amount' ? net(inv) : (inv[field] ?? 0), inv.currency), 0);
    }
    function sumPaid(arr) {
      return arr.reduce((s, inv) => {
        const gross = Number(inv.total_amount) || 0;
        const share = gross ? net(inv) / gross : 1;
        return s + cxInv(invoicePaidAmount(inv) * share, inv.currency);
      }, 0);
    }

    return {
      dueInMonth,
      issuedInMonth,
      issuedBeforeDueIn,
      issuedInDueLater,
      noDueDate,
      noIssueDate,
      dueVisFallback,
      byDue: {
        invoiced: sumField(dueInMonth, 'total_amount'),
        paid: sumPaid(dueInMonth),
        count: dueInMonth.length,
      },
      byIssue: {
        invoiced: sumField(issuedInMonth, 'total_amount'),
        paid: sumPaid(issuedInMonth),
        count: issuedInMonth.length,
      },
      decomposition: {
        issuedBeforeDueInInvoiced: sumField(issuedBeforeDueIn, 'total_amount'),
        issuedBeforeDueInCount: issuedBeforeDueIn.length,
        issuedInDueLaterInvoiced: sumField(issuedInDueLater, 'total_amount'),
        issuedInDueLaterCount: issuedInDueLater.length,
        noDueDateCount: noDueDate.length,
        dueVisFallbackInvoiced: sumField(dueVisFallback, 'total_amount'),
        dueVisFallbackCount: dueVisFallback.length,
      },
      issuedWithPaid: issuedInMonth.filter(inv => invoicePaidAmount(inv) > 0),
    };
  }, [invoices, cxInv, targetMonth]);

  if (!recon) return null;

  const monthLabel = (() => {
    const [y, m] = targetMonth.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(m, 10) - 1]} ${y}`;
  })();

  return (
    <Card data-testid="invoice-reconciliation-card">
      <CardHeader>
        <CardTitle className="text-base">
          {monthLabel} Reconciliation: Due-date vs Issue-date
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Same invoices, two bucketing bases. Paid = sum of amount_paid on both surfaces.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Side-by-side comparison */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border rounded-lg p-3">
            <p className="text-xs font-medium text-muted-foreground mb-1">By Due Date</p>
            <p className="text-xs text-muted-foreground">(Cashflow chart, Revenue Invoices vs Payments)</p>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span>Invoiced</span>
                <span className="font-mono font-medium">{formatCurrency(recon.byDue.invoiced, dc)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                <span>Paid</span>
                <span className="font-mono font-medium">{formatCurrency(recon.byDue.paid, dc)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Invoices</span>
                <span>{recon.byDue.count}</span>
              </div>
            </div>
          </div>
          <div className="border rounded-lg p-3">
            <p className="text-xs font-medium text-muted-foreground mb-1">By Issue Date</p>
            <p className="text-xs text-muted-foreground">(Invoices tab, P&L Invoices)</p>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span>Invoiced</span>
                <span className="font-mono font-medium">{formatCurrency(recon.byIssue.invoiced, dc)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                <span>Paid</span>
                <span className="font-mono font-medium">{formatCurrency(recon.byIssue.paid, dc)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Invoices</span>
                <span>{recon.byIssue.count}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Population decomposition */}
        <div className="border rounded-lg p-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Population Decomposition</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span>Issued before {monthLabel}, due in {monthLabel}</span>
              <span className="font-mono">
                {recon.decomposition.issuedBeforeDueInCount} inv, {formatCurrency(recon.decomposition.issuedBeforeDueInInvoiced, dc)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Issued in {monthLabel}, due later or different month</span>
              <span className="font-mono">
                {recon.decomposition.issuedInDueLaterCount} inv, {formatCurrency(recon.decomposition.issuedInDueLaterInvoiced, dc)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>No due date, issued in {monthLabel} (due-date surface via fallback)</span>
              <span className="font-mono">
                {recon.decomposition.dueVisFallbackCount} inv, {formatCurrency(recon.decomposition.dueVisFallbackInvoiced, dc)}
              </span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Total invoices without due date (all months)</span>
              <span className="font-mono">{recon.decomposition.noDueDateCount}</span>
            </div>
          </div>
        </div>

        {/* June-issued invoices with amount_paid */}
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-muted-foreground">
              {monthLabel}-issued invoices with amount_paid {'>'} 0: {recon.issuedWithPaid.length}
            </p>
            {recon.issuedWithPaid.length > 0 && (
              <button
                className="text-xs text-primary hover:underline flex items-center gap-0.5"
                onClick={() => setShowDetail(!showDetail)}
              >
                {showDetail ? 'Hide' : 'Show'}
                {showDetail
                  ? <ChevronUp className="h-3 w-3" />
                  : <ChevronDown className="h-3 w-3" />}
              </button>
            )}
          </div>
          {recon.issuedWithPaid.length === 0 && (
            <p className="text-xs text-muted-foreground">
              All {monthLabel}-issued invoices have amount_paid = 0. Paid = 0 on issue-date surfaces is correct.
            </p>
          )}
          {showDetail && recon.issuedWithPaid.length > 0 && (
            <div className="overflow-x-auto mt-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Invoice</TableHead>
                    <TableHead className="text-xs">Client</TableHead>
                    <TableHead className="text-xs text-right">Total</TableHead>
                    <TableHead className="text-xs text-right">Paid</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Issue</TableHead>
                    <TableHead className="text-xs">Due</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recon.issuedWithPaid.map((inv, i) => (
                    <TableRow key={inv.bros_id || i} className="text-xs">
                      <TableCell className="font-mono py-1">{inv.bros_id || '-'}</TableCell>
                      <TableCell className="py-1">{inv.company || '-'}</TableCell>
                      <TableCell className="text-right font-mono py-1">
                        {formatCurrency(cxInv(inv.total_amount, inv.currency), dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono py-1 text-green-600 dark:text-green-400">
                        {formatCurrency(cxInv(invoicePaidAmount(inv), inv.currency), dc)}
                      </TableCell>
                      <TableCell className="py-1">{inv.status || '-'}</TableCell>
                      <TableCell className="py-1">{inv.issue_date || '-'}</TableCell>
                      <TableCell className="py-1">{inv.due_date || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
